import eggs from './eggs'
import {
  celebrateCompletion,
  removeCompletionGlow
} from './effects/completion'
import { playConfetti } from './effects/confetti'
import { playGlow } from './effects/glow'
import { playLights } from './effects/lights'
import { playSheep } from './effects/sheep'
import { playSound, primeSound } from './effects/sound'

export type EffectConfig =
  | { type: 'glow'; color?: string; durationMs?: number }
  | {
      type: 'sound'
      src?: string
      volume?: number
      synth?: 'chime' | 'pop' | 'fanfare'
      fadeOutMs?: number
    }
  | { type: 'confetti'; particleCount?: number; durationMs?: number }
  | { type: 'sheep'; durationMs?: number }
  | { type: 'lights'; durationMs?: number }

export type EasterEgg = {
  /** Unique slug, doubles as the storage key in localStorage */
  slug: string
  /** Search terms that trigger the egg (matched case- and accent-insensitively) */
  terms?: string[]
  /**
   * 'exact' (default): the whole query must equal a term.
   * 'contains': a word in the query must equal or start with a term,
   * so 'hirte' also matches "der gute Hirte" and "Hirten".
   */
  match?: 'exact' | 'contains'
  /** Widget sources allowed to trigger it (e.g. 'location-finder'); omit for all */
  sources?: string[]
  /**
   * When search eggs may fire: only when the search came up empty
   * ('no-results', default) or regardless of results ('always').
   */
  when?: 'no-results' | 'always'
  effects: EffectConfig[]
  /**
   * How often the effects replay (default 'always'). The found-state in
   * localStorage is recorded on the first trigger regardless.
   */
  repeat?: 'always' | 'session' | 'once'
}

const foundStorageKey = 'icf-easter-eggs-found'
const sessionStorageKey = 'icf-easter-eggs-played'
/**
 * Everything reset() wipes, by key prefix rather than by individual key, so new
 * state joins in automatically. Keep new keys on one of these prefixes.
 *
 * - `icf-easter-egg`: this engine, effects/completion.ts and the per-block
 *   scripts (e.g. the footer's logo round counter)
 * - `icf-sheep-run`: the mini-game's fleece badges. Included because the
 *   'sheep-champion' egg hangs off the badge set - keeping the badges would
 *   hand that egg back for a single easy win instead of all levels again.
 */
const storagePrefixes = ['icf-easter-egg', 'icf-sheep-run']

function readRecord(storage: Storage, key: string): Record<string, string> {
  try {
    const raw = storage.getItem(key)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, string>)
      : {}
  } catch {
    return {}
  }
}

function writeRecord(
  storage: Storage,
  key: string,
  value: Record<string, string>
) {
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch {
    // storage unavailable (e.g. private mode) - egg still plays, just isn't remembered
  }
}

function normalize(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

function mayPlay(egg: EasterEgg): boolean {
  const repeat = egg.repeat ?? 'always'
  if (repeat === 'always') return true
  if (repeat === 'once') {
    return !(egg.slug in readRecord(localStorage, foundStorageKey))
  }
  return !(egg.slug in readRecord(sessionStorage, sessionStorageKey))
}

function runEffect(effect: EffectConfig) {
  switch (effect.type) {
    case 'glow':
      playGlow(effect)
      break
    case 'sound':
      playSound(effect)
      break
    case 'confetti':
      playConfetti(effect)
      break
    case 'sheep':
      playSheep(effect)
      break
    case 'lights':
      playLights(effect)
      break
  }
}

/**
 * Per-slug "still running" lock. A long egg (the Konami/shake "worship moment"
 * runs its lights, clip and confetti for ~13s) must not be re-triggered while
 * it plays - a second shake would stack another full run on top and the audio,
 * lights and confetti would phase against themselves. We block re-entry until
 * the longest of the egg's own effect durations has elapsed.
 */
const busyUntil: Record<string, number> = {}

function lockDurationMs(egg: EasterEgg): number {
  let max = 0
  for (const effect of egg.effects) {
    const duration = 'durationMs' in effect ? (effect.durationMs ?? 0) : 0
    if (duration > max) max = duration
  }
  return max
}

function runEgg(egg: EasterEgg, source?: string) {
  const now = Date.now()
  if (now < (busyUntil[egg.slug] ?? 0)) return
  if (!mayPlay(egg)) return

  const lockMs = lockDurationMs(egg)
  if (lockMs > 0) busyUntil[egg.slug] = now + lockMs

  const timestamp = new Date().toISOString()
  writeRecord(sessionStorage, sessionStorageKey, {
    ...readRecord(sessionStorage, sessionStorageKey),
    [egg.slug]: timestamp
  })
  const found = readRecord(localStorage, foundStorageKey)
  if (!(egg.slug in found)) {
    writeRecord(localStorage, foundStorageKey, {
      ...found,
      [egg.slug]: timestamp
    })
  }

  window.dataLayer?.push({
    event: 'easter_egg_found',
    easter_egg_slug: egg.slug,
    easter_egg_source: source ?? 'manual'
  })

  for (const effect of egg.effects) {
    runEffect(effect)
  }

  // the last egg pays out on top of its own effects
  const state = progress()
  if (state.complete) {
    celebrateCompletion(state.found)
  }
}

function matchesQuery(egg: EasterEgg, query: string): boolean {
  if (!egg.terms) return false
  if ((egg.match ?? 'exact') === 'exact') {
    return egg.terms.some((term) => normalize(term) === query)
  }
  const words = query.split(/[^\p{L}\p{N}]+/u).filter(Boolean)
  return egg.terms.some((term) => {
    const normalized = normalize(term)
    return words.some((word) => word.startsWith(normalized))
  })
}

/** Check a search query against all term-based eggs and play any match. */
export function evaluateSearch(context: {
  source: string
  query: string
  noResults: boolean
}) {
  const query = normalize(context.query)
  if (!query) return
  for (const egg of eggs) {
    if ((egg.when ?? 'no-results') === 'no-results' && !context.noResults)
      continue
    if (!matchesQuery(egg, query)) continue
    if (egg.sources && !egg.sources.includes(context.source)) continue
    runEgg(egg, context.source)
  }
}

/**
 * Unlock every clip-backed egg sound for gesture-less playback (iOS). Call from
 * inside a real user gesture - e.g. the footer-logo arm tap that also enables
 * shake - so a later shake, which is not a gesture, can still play the clip.
 */
export function primeSounds() {
  for (const egg of eggs) {
    for (const effect of egg.effects) {
      if (effect.type === 'sound' && effect.src) primeSound(effect.src)
    }
  }
}

/** Trigger an egg directly by slug, from any component (React or not). */
export function trigger(slug: string, source?: string) {
  const egg = eggs.find((candidate) => candidate.slug === slug)
  if (egg) runEgg(egg, source)
}

function clearPrefixed(storage: Storage) {
  // collect first: removing while iterating shifts the indices
  const keys: string[] = []
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index)
    if (key && storagePrefixes.some((prefix) => key.startsWith(prefix))) {
      keys.push(key)
    }
  }
  for (const key of keys) {
    storage.removeItem(key)
  }
}

/**
 * Development helper: wipe the hunt for this browser - found eggs, the
 * session's played record, the celebration marker, the per-block counters and
 * the mini-game's badges (see storagePrefixes). No reload needed.
 *
 * Exposed as `window.icfEasterEggs.reset()` (see the navbar view) so it can be
 * run from the browser console. Nothing calls it automatically: found eggs are
 * kept for good, and a new egg invalidates the completion reward on its own
 * (see effects/completion.ts).
 */
export function reset() {
  try {
    clearPrefixed(localStorage)
    clearPrefixed(sessionStorage)
  } catch {
    // storage unavailable - nothing was stored either, so nothing to clear
  }
  removeCompletionGlow()
}

/** Found-state across all registered eggs, e.g. for a future reward display. */
export function progress(): {
  found: string[]
  total: number
  complete: boolean
} {
  const found = readRecord(localStorage, foundStorageKey)
  const foundSlugs = eggs
    .filter((egg) => egg.slug in found)
    .map((egg) => egg.slug)
  return {
    found: foundSlugs,
    total: eggs.length,
    complete: eggs.length > 0 && foundSlugs.length === eggs.length
  }
}
